import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react'
import classNames from 'classnames'
import { Alert, Tab } from 'react-bootstrap'
import { type RJSFSchema } from '@rjsf/utils'
import { Tabs } from '../../Tabs/Tabs'
import { Spinner } from '../../Spinner/Spinner'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import { selectAuthenticatedUser } from '../../../store/auth/authSlice'
import { addToast } from '../../../store/toast/toastSlice'
import { useZocksAuth } from '../../ScopedBlock/hooks'
import {
  useDeleteAccountParametersMutation,
  useDeleteUserParametersMutation,
  useGetCustomWorkflowParametersQuery,
  usePutAccountParametersMutation,
  usePutUserParametersMutation,
  type CustomWorkflowParametersView,
} from '../../../store/customWorkflow/customWorkflows'
import { applyMergePatch, deepEqual, defaultsFromSchema } from './mergePatch'
import { WorkflowParametersForm, type WorkflowParametersFormHandle } from './WorkflowParametersForm'
import { EffectiveParametersView } from './EffectiveParametersView'

export interface WorkflowParametersSectionProps {
  accountId: string
  workflowId: string
  onHasParameters: (has: boolean) => void
  onFooterStateChange?: (state: ParametersFooterState) => void
}

export interface ParametersFooterState {
  dirty: boolean
  saving: boolean
  blocked: boolean
}

export interface WorkflowParametersSectionHandle {
  save: () => Promise<boolean>
}

interface LayerConfig {
  schema: RJSFSchema
  baseline: Record<string, unknown>
  layer: Record<string, unknown> | null
}

const detailFromError = (e: unknown): string | null => {
  if (typeof e === 'object' && e !== null && 'data' in e) {
    const data = (e as { data?: { detail?: unknown; errors?: Array<{ message?: unknown }> } }).data
    const detail = data?.detail
    if (detail !== undefined && detail !== null) {
      return String(detail)
    }
    if (Array.isArray(data?.errors)) {
      const messages = data.errors.map((error) => error?.message).filter((message): message is string => typeof message === 'string')
      if (messages.length > 0) {
        return messages.join(', ')
      }
    }
  }
  return null
}

export const WorkflowParametersSection = forwardRef<WorkflowParametersSectionHandle, WorkflowParametersSectionProps>((props, ref) => {
  const currentUserId = useAppSelector(selectAuthenticatedUser)?.id
  return <WorkflowParametersSectionContent key={`${props.accountId}|${props.workflowId}|${currentUserId}`} ref={ref} {...props} />
})
WorkflowParametersSection.displayName = 'WorkflowParametersSection'

const WorkflowParametersSectionContent = forwardRef<WorkflowParametersSectionHandle, WorkflowParametersSectionProps>(function WorkflowParametersSectionContent(
  { accountId, workflowId, onHasParameters, onFooterStateChange },
  ref,
) {
  const userFormRef = useRef<WorkflowParametersFormHandle>(null)
  const accountFormRef = useRef<WorkflowParametersFormHandle>(null)
  const dispatch = useAppDispatch()
  const currentUserId = useAppSelector(selectAuthenticatedUser)?.id
  const { hasScope } = useZocksAuth()
  const isAdmin = hasScope('**', 'custom_workflow:admin')

  const { data, isLoading, isError, error } = useGetCustomWorkflowParametersQuery({
    accountId,
    workflowId,
    userId: currentUserId,
    suppressGlobalErrorToast: true,
  })
  const isNotFound = isError && typeof error === 'object' && error !== null && 'status' in error && error.status === 404

  const [putAccountParameters, { isLoading: savingAccount }] = usePutAccountParametersMutation()
  const [deleteAccountParameters, { isLoading: resettingAccount }] = useDeleteAccountParametersMutation()
  const [putUserParameters, { isLoading: savingUser }] = usePutUserParametersMutation()
  const [deleteUserParameters, { isLoading: resettingUser }] = useDeleteUserParametersMutation()

  const [activeTab, setActiveTab] = useState<'account' | 'user'>('user')
  const [effectiveOpen, setEffectiveOpen] = useState(false)
  const [accountDirty, setAccountDirty] = useState(false)
  const [userDirty, setUserDirty] = useState(false)
  const [accountUploading, setAccountUploading] = useState(false)
  const [userUploading, setUserUploading] = useState(false)
  const [accountExternallyUpdated, setAccountExternallyUpdated] = useState(false)
  const [userExternallyUpdated, setUserExternallyUpdated] = useState(false)
  const [trackedData, setTrackedData] = useState<CustomWorkflowParametersView | undefined>(data)

  if (data !== trackedData) {
    const previousData = trackedData
    setTrackedData(data)
    const accountLayerChanged = !deepEqual(previousData?.accountParameters, data?.accountParameters)
    const userLayerChanged = !deepEqual(previousData?.userParameters, data?.userParameters)
    if (accountDirty && accountLayerChanged) {
      setAccountExternallyUpdated(true)
    }
    if (userDirty && (userLayerChanged || accountLayerChanged)) {
      setUserExternallyUpdated(true)
    }
  }

  if (!accountDirty && accountExternallyUpdated) {
    setAccountExternallyUpdated(false)
  }
  if (!userDirty && userExternallyUpdated) {
    setUserExternallyUpdated(false)
  }

  const hasParameters = data ? true : isNotFound ? false : null
  useEffect(() => {
    if (hasParameters !== null) {
      onHasParameters(hasParameters)
    }
  }, [hasParameters, onHasParameters])

  const dirty = accountDirty || userDirty

  const saving = savingAccount || resettingAccount || savingUser || resettingUser
  const blocked = accountUploading || userUploading
  useEffect(() => {
    onFooterStateChange?.({ dirty, saving, blocked })
  }, [dirty, saving, blocked, onFooterStateChange])

  useImperativeHandle(
    ref,
    () => ({
      save: () => {
        const dirtyForms = [
          { tab: 'user' as const, form: userFormRef.current, dirty: userDirty },
          { tab: 'account' as const, form: accountFormRef.current, dirty: accountDirty },
        ].filter((entry) => entry.dirty)
        const validated = dirtyForms.map((entry) => ({ ...entry, valid: entry.form?.validate() ?? false }))
        const firstInvalid = validated.find((entry) => !entry.valid)
        if (firstInvalid) {
          setActiveTab(firstInvalid.tab)
          return Promise.resolve(false)
        }
        return [...dirtyForms]
          .sort((a, b) => (a.tab === 'account' ? -1 : b.tab === 'account' ? 1 : 0))
          .reduce<Promise<boolean>>(
            (previous, entry) =>
              previous.then((saved) => {
                if (saved) {
                  return entry.form?.save() ?? Promise.resolve(false)
                }
                dispatch(
                  addToast({ variant: 'error', title: 'Your own values were not saved', messages: ['Save again once the account-level values are saved.'] }),
                )
                return false
              }),
            Promise.resolve(true),
          )
      },
    }),
    [userDirty, accountDirty, dispatch],
  )

  const schema = data?.parameterSchema

  const layers = useMemo(() => {
    if (!data || !schema) {
      return null
    }
    const defaults = defaultsFromSchema(schema)
    const account: LayerConfig = { schema, baseline: defaults, layer: data.accountParameters }
    const user: LayerConfig = {
      schema,
      baseline: applyMergePatch(defaults, data.accountParameters ?? {}),
      layer: data.userParameters,
    }
    return { account, user }
  }, [data, schema])

  const handleSaveAccount = async (patch: Record<string, unknown>): Promise<boolean> => {
    try {
      await putAccountParameters({ accountId, workflowId, parameters: patch, suppressGlobalErrorToast: true }).unwrap()
      return true
    } catch (e) {
      dispatch(addToast({ variant: 'error', title: 'Could not save parameters', messages: [detailFromError(e) ?? 'Please try again.'] }))
      return false
    }
  }

  const handleResetAccount = async (): Promise<boolean> => {
    try {
      await deleteAccountParameters({ accountId, workflowId, suppressGlobalErrorToast: true }).unwrap()
      return true
    } catch (e) {
      dispatch(addToast({ variant: 'error', title: 'Could not reset parameters', messages: [detailFromError(e) ?? 'Please try again.'] }))
      return false
    }
  }

  const handleSaveUser = async (patch: Record<string, unknown>): Promise<boolean> => {
    if (!currentUserId) {
      dispatch(addToast({ variant: 'error', title: 'Could not save parameters', messages: ['Please try again.'] }))
      return false
    }
    try {
      await putUserParameters({ accountId, workflowId, userId: currentUserId, parameters: patch, suppressGlobalErrorToast: true }).unwrap()
      return true
    } catch (e) {
      dispatch(addToast({ variant: 'error', title: 'Could not save parameters', messages: [detailFromError(e) ?? 'Please try again.'] }))
      return false
    }
  }

  const handleResetUser = async (): Promise<boolean> => {
    if (!currentUserId) {
      dispatch(addToast({ variant: 'error', title: 'Could not reset parameters', messages: ['Please try again.'] }))
      return false
    }
    try {
      await deleteUserParameters({ accountId, workflowId, userId: currentUserId, suppressGlobalErrorToast: true }).unwrap()
      return true
    } catch (e) {
      dispatch(addToast({ variant: 'error', title: 'Could not reset parameters', messages: [detailFromError(e) ?? 'Please try again.'] }))
      return false
    }
  }

  if (isLoading) {
    return <Spinner size='sm' />
  }

  if (isNotFound && !data) {
    return null
  }

  if (isError && !isNotFound && !data) {
    return (
      <Alert variant='warning' className='mb-0'>
        Could not load parameters.
      </Alert>
    )
  }

  if (!data || !layers) {
    return null
  }

  return (
    <div className='border-top pt-3'>
      {isError ? (
        <Alert variant='warning' className='mb-2'>
          Could not refresh parameters — showing the last loaded values.
        </Alert>
      ) : null}
      <div className='fw-semibold'>Parameters</div>
      <div className='paragraph-small zocks-text-gray-600 mb-2'>Customize how this workflow behaves. Your own values override account-level values.</div>
      <Tabs id='workflow-parameters-tabs' activeKey={activeTab} onSelect={(key) => setActiveTab(key === 'account' ? 'account' : 'user')}>
        <Tab eventKey='user' title='Your own' />
        {isAdmin ? <Tab eventKey='account' title='Account level' /> : null}
      </Tabs>
      <div data-testid='workflow-parameters-pane-user' className={classNames('pt-3', { 'd-none': activeTab !== 'user' })}>
        <WorkflowParametersForm
          idPrefix='user'
          layerName='user'
          schema={layers.user.schema}
          baseline={layers.user.baseline}
          layer={layers.user.layer}
          saving={savingUser || resettingUser}
          onSave={handleSaveUser}
          onReset={handleResetUser}
          onDirtyChange={setUserDirty}
          onUploadingChange={setUserUploading}
          ref={userFormRef}
          externallyUpdated={userExternallyUpdated}
          onAcknowledgeExternalUpdate={() => setUserExternallyUpdated(false)}
          onInvalid={() => setActiveTab('user')}
        />
      </div>
      {isAdmin ? (
        <div data-testid='workflow-parameters-pane-account' className={classNames('pt-3', { 'd-none': activeTab !== 'account' })}>
          <WorkflowParametersForm
            idPrefix='account'
            layerName='account'
            schema={layers.account.schema}
            baseline={layers.account.baseline}
            layer={layers.account.layer}
            saving={savingAccount || resettingAccount}
            onSave={handleSaveAccount}
            onReset={handleResetAccount}
            onDirtyChange={setAccountDirty}
            onUploadingChange={setAccountUploading}
            ref={accountFormRef}
            externallyUpdated={accountExternallyUpdated}
            onAcknowledgeExternalUpdate={() => setAccountExternallyUpdated(false)}
            onInvalid={() => setActiveTab('account')}
          />
        </div>
      ) : null}
      {data.effective.warnings.length > 0 ? (
        <Alert variant='warning' className='mt-3 mb-0'>
          {data.effective.warnings.map((warning, index) => (
            <div key={index}>{warning}</div>
          ))}
        </Alert>
      ) : null}
      <details className='mt-3' onToggle={(event) => setEffectiveOpen(event.currentTarget.open)}>
        <summary>Effective configuration</summary>
        {effectiveOpen ? (
          <div className='pt-3'>
            <EffectiveParametersView schema={layers.account.schema} parameters={data.effective.parameters} />
          </div>
        ) : null}
      </details>
    </div>
  )
})
